import { ReactNode, useEffect, useMemo, useState } from 'react';
import { AxiosError } from 'axios';
import { useApiClient } from '@/context/ApiClientContext/useApiClient.ts';
import { useNavigate } from 'react-router-dom';
import { get_run_url } from '@/utils/urls.ts';
import { ActiveStatuses } from '@/types/statuses.ts';
import { Run } from '@/types/runs.ts';
import {
  WATCHED_RUNS_CHANGED_EVENT,
  WATCHED_RUNS_STORAGE_KEY,
  WatchedRun,
  getWatchedRuns,
  replaceWatchedRuns,
} from '@/features/runBrowserNotifications/watchedRuns.ts';
import {
  didRunReachNotifiableStatus,
  showRunBrowserNotification,
} from '@/features/runBrowserNotifications/notifications.ts';

const RUN_BROWSER_NOTIFICATION_POLL_INTERVAL_MS = 15_000;

function isNotFoundError(error: unknown) {
  return error instanceof AxiosError && error.response?.status === 404;
}

export default function RunBrowserNotificationProvider({ children }: { children: ReactNode }) {
  const apiClient = useApiClient();
  const navigate = useNavigate();
  const [watchedRuns, setWatchedRuns] = useState<WatchedRun[]>(getWatchedRuns);

  useEffect(() => {
    const syncWatchedRuns = () => setWatchedRuns(getWatchedRuns());
    const onStorage = (event: StorageEvent) => {
      if (
        event.storageArea === window.localStorage &&
        (event.key === WATCHED_RUNS_STORAGE_KEY || event.key === null)
      ) {
        syncWatchedRuns();
      }
    };

    window.addEventListener('storage', onStorage);
    window.addEventListener(WATCHED_RUNS_CHANGED_EVENT, syncWatchedRuns);

    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(WATCHED_RUNS_CHANGED_EVENT, syncWatchedRuns);
    };
  }, []);

  const watchedRunIds = useMemo(
    () => watchedRuns.map((watchedRun) => watchedRun.runId).sort().join('|'),
    [watchedRuns],
  );

  useEffect(() => {
    if (watchedRuns.length === 0) return;

    let cancelled = false;

    const pollWatchedRuns = async () => {
      const runsToCheck = getWatchedRuns();
      if (runsToCheck.length === 0) {
        setWatchedRuns([]);
        return;
      }

      const results = await Promise.allSettled(
        runsToCheck.map(async (watchedRun) => {
          const response = await apiClient.get<Run>(get_run_url(watchedRun.runId));
          return { watchedRun, run: response.data };
        }),
      );

      if (cancelled) return;

      const nextWatchedRunsById = new Map(
        getWatchedRuns().map((watchedRun) => [watchedRun.runId, watchedRun] as const),
      );

      results.forEach((result, index) => {
        const polledWatchedRun = runsToCheck[index];
        const latestWatchedRun = nextWatchedRunsById.get(polledWatchedRun.runId);
        if (!latestWatchedRun) {
          return;
        }

        if (result.status === 'rejected') {
          if (isNotFoundError(result.reason)) {
            nextWatchedRunsById.delete(polledWatchedRun.runId);
          }
          return;
        }

        const { run } = result.value;
        if (didRunReachNotifiableStatus(latestWatchedRun.lastKnownStatus, run.status)) {
          showRunBrowserNotification({
            automationId: latestWatchedRun.automationId,
            run,
            navigate,
          });
        }

        if (ActiveStatuses.includes(run.status)) {
          nextWatchedRunsById.set(polledWatchedRun.runId, {
            ...latestWatchedRun,
            runName: run.name,
            lastKnownStatus: run.status,
          });
        } else {
          nextWatchedRunsById.delete(polledWatchedRun.runId);
        }
      });

      const nextWatchedRuns = Array.from(nextWatchedRunsById.values());
      replaceWatchedRuns(nextWatchedRuns);
      setWatchedRuns(nextWatchedRuns);
    };

    void pollWatchedRuns();
    const intervalId = window.setInterval(() => {
      void pollWatchedRuns();
    }, RUN_BROWSER_NOTIFICATION_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [apiClient, navigate, watchedRunIds, watchedRuns.length]);

  return <>{children}</>;
}
